import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { WholesaleStatsDTO } from '@bifeai/shared';
import { BarChart, Card, CardBody, CardHeader, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { Building2, HandCoins, Package, Receipt, Scale, TrendingUp, UserPlus, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { todayIso } from '../../lib/units';
import { PeriodPicker, type Period } from '../DashboardPage';

const DAYS_OF: Record<string, number> = { today: 1, '7d': 7, '15d': 15, '30d': 30, '60d': 60, '90d': 90, '6m': 182, '1y': 365 };
const num = (v: number) => v.toLocaleString('es-AR');

function rangeOf(p: Period): { from: string; to: string } {
  if (p.range === 'custom') return { from: p.from, to: p.to };
  const to = todayIso();
  const from = new Date(new Date(`${to}T12:00:00Z`).getTime() - ((DAYS_OF[p.range] ?? 30) - 1) * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

/** Números del canal mayorista, separados de las ventas del mostrador. */
export function WholesaleStatsPage() {
  const [period, setPeriod] = useState<Period>({ range: '30d', from: todayIso(), to: todayIso() });
  const r = rangeOf(period);
  const q = useQuery({
    queryKey: ['wholesale', 'stats', r.from, r.to],
    queryFn: () => api.get<WholesaleStatsDTO>(`/wholesale/stats?from=${r.from}&to=${r.to}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const table = (title: string, rows: { name: string; orders: number; soldCents: number; extra?: string }[]) => (
    <Card>
      <CardHeader title={title} />
      <CardBody>
        {rows.length === 0 ? (
          <p className="bf-cell-sub">Sin ventas en el período.</p>
        ) : (
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th />
                  <th style={{ textAlign: 'right' }}>Pedidos</th>
                  <th style={{ textAlign: 'right' }}>Vendido</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => (
                  <tr key={x.name}>
                    <td>
                      {x.name}
                      {x.extra && <span className="bf-cell-sub"> · {x.extra}</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{x.orders > 0 ? num(x.orders) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(x.soldCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
  return (
    <>
      <PageHeader title="Estadísticas del mayorista" subtitle="Ventas por mayor, cobranza y deuda. No se mezclan con las ventas del mostrador." />
      <PeriodPicker value={period} onChange={setPeriod} />
      {!d ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Vendido por mayor"
              tone="green"
              icon={<TrendingUp />}
              value={formatMoney(d.soldCents)}
              delta={`${num(d.orders)} pedidos`}
              deltaMuted
            />
            <Kpi
              label="Kilos"
              tone="celeste"
              icon={<Scale />}
              value={`${num(d.kilos)} kg`}
              delta={`Ticket promedio ${formatMoney(d.avgTicketCents)}`}
              deltaMuted
            />
            <Kpi label="Cobrado" tone="amber" icon={<Wallet />} value={formatMoney(d.collectedCents)} delta="Todos los medios" deltaMuted />
            <Kpi
              label="Deuda en la calle"
              tone="red"
              icon={<HandCoins />}
              value={formatMoney(d.debtCents)}
              delta={`Vencida ${formatMoney(d.overdueCents)}`}
              deltaMuted
            />
          </div>
          <div className="bf-kpi-grid">
            <Kpi label="Clientes que compraron" tone="celeste" icon={<Building2 />} value={num(d.activeCustomers)} delta="En el período" deltaMuted />
            <Kpi
              label="Clientes nuevos"
              tone="green"
              icon={<UserPlus />}
              value={num(d.newCustomers)}
              delta="Dados de alta en el período"
              deltaMuted
            />
            <Kpi label="Pedidos" tone="amber" icon={<Receipt />} value={num(d.orders)} delta="Preparados" deltaMuted />
            <Kpi
              label="Productos distintos"
              tone="red"
              icon={<Package />}
              value={num(d.topProducts.length)}
              delta="Entre los más vendidos"
              deltaMuted
            />
          </div>
          <Card>
            <CardHeader title="Vendido por día" subtitle={`${formatDate(d.period.from)} al ${formatDate(d.period.to)}`} />
            <CardBody>
              <BarChart
                ariaLabel="Vendido por mayor por día"
                data={d.byDay.map((x) => ({ key: x.date, label: formatDate(x.date).slice(0, 5), value: x.soldCents }))}
                tip={(i) => {
                  const x = d.byDay[i]!;
                  return { title: formatDate(x.date), lines: [formatMoney(x.soldCents), `${num(x.orders)} pedidos`] };
                }}
              />
            </CardBody>
          </Card>
          <div className="bf-dash-grid">
            {table('Por cliente', d.byCustomer)}
            {table(
              'Por vendedor',
              d.bySeller.map((s) => ({ ...s, extra: s.newCustomers ? `${s.newCustomers} clientes nuevos` : undefined })),
            )}
          </div>
          <div className="bf-dash-grid">
            {table('Por zona', d.byZone)}
            {table('Por tipo de negocio', d.bySegment)}
          </div>
          <div className="bf-dash-grid">
            {table('Por lista de precios', d.byPriceList)}
            {table(
              'Lo más vendido',
              d.topProducts.map((p) => ({
                name: p.name,
                orders: 0,
                soldCents: p.soldCents,
                extra: p.kilos ? `${num(p.kilos)} kg` : `${num(p.units)} u.`,
              })),
            )}
          </div>
        </>
      )}
    </>
  );
}
